import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Magic Link State
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // CRITICAL: Prevent multiple Magic Link callback processing
  const magicLinkProcessedRef = useRef(false);
  const isProcessingLoginRef = useRef(false);

  // KRITISCH: Prüfe ob wir von einem Magic Link kommen
  useEffect(() => {
    const checkMagicLinkCallback = async () => {
      // CRITICAL: Only process once
      if (magicLinkProcessedRef.current) {
        console.log('[Login] ⚠️ Magic Link already processed - skipping');
        return;
      }

      // Check URL hash for Supabase Auth Callback
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      
      if (accessToken && type === 'magiclink') {
        // Mark as processed immediately
        magicLinkProcessedRef.current = true;

        console.log('[Login] 🎯 Magic Link Callback detected!');
        
        try {
          // Remove hash from URL immediately
          console.log('[Login] 🧹 Cleaning URL hash...');
          window.history.replaceState(null, '', window.location.pathname);
          
          // Wait briefly for session initialization
          console.log('[Login] ⏳ Waiting for session...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Check session
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError || !session) {
            console.error('[Login] ❌ Session error after Magic Link');
            setError('Login failed. Please try again.');
            magicLinkProcessedRef.current = false; // Reset on error
            return;
          }
          
          console.log('[Login] ✅ Session present - Redirecting to dashboard...');
          
          // Direct navigation
          navigate('/dashboard', { replace: true });
          
        } catch (err: any) {
          console.error('[Login] ❌ Magic Link Callback Error:', err);
          setError('An error occurred. Please try again.');
          magicLinkProcessedRef.current = false; // Reset on error
        }
      }
    };
    
    checkMagicLinkCallback();
  }, [location, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // CRITICAL: Prevent multiple submissions
    if (isProcessingLoginRef.current) {
      console.log('[Login] ⚠️ Login already in progress - skipping');
      return;
    }

    isProcessingLoginRef.current = true;
    setError('');
    setLoading(true);

    try {
      console.log(`[Login] 🔐 Step 1/4: Starting password login for: ${email}`);

      // STEP 1: Call the login-user Edge Function
      console.log('[Login] 📡 Step 2/4: Calling login-user Edge Function...');
      
      const { data: loginData, error: functionError } = await supabase.functions.invoke('login-user', {
        body: {
          email,
          password,
        },
      });

      // Handle Edge Function errors (network issues, etc.)
      if (functionError) {
        console.error('[Login] ❌ Edge Function Error:', functionError);
        
        // Try to extract error message from the response
        let errorMessage = 'Login failed. Please try again.';
        
        // Check if there's a response body with error details
        if (functionError.message) {
          errorMessage = functionError.message;
        }
        
        throw new Error(errorMessage);
      }

      // Check if login failed (invalid credentials)
      if (!loginData || !loginData.success) {
        console.warn('[Login] ⚠️ Login failed:', loginData?.message || 'Unknown error');
        
        // Show user-friendly error message
        const errorMsg = loginData?.error || loginData?.message || 'Invalid email or password. Please check your credentials.';
        
        if (loginData?.redirect_to_register) {
          setError('Email or password not found. Redirecting to registration...');
          setTimeout(() => {
            navigate('/register');
          }, 2000);
          return;
        }
        
        throw new Error(errorMsg);
      }

      // STEP 2: Login successful - set session
      console.log('[Login] ✅ Step 3/4: Login successful!', loginData.user);
      
      // Set the session in Supabase client
      if (loginData.session) {
        console.log('[Login] 🔑 Step 4/4: Setting session in Supabase client...');
        
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: loginData.session.access_token,
          refresh_token: loginData.session.refresh_token,
        });

        if (sessionError) {
          console.error('[Login] ❌ Session Error:', sessionError);
          throw sessionError;
        }
        console.log('[Login] ✅ Session set successfully');
      }

      console.log('[Login] 🎉 Authentication complete! Redirecting to dashboard...');
      
      // ✅ Direct navigation without state management
      navigate('/dashboard', { replace: true });

    } catch (err: any) {
      console.error('[Login] ❌ Error:', err);
      
      // Show user-friendly error message
      let displayError = err.message || 'Login failed';
      
      // Replace technical error messages with user-friendly ones
      if (displayError.includes('FunctionsHttpError') || displayError.includes('non-2xx')) {
        displayError = 'Invalid email or password. Please check your credentials.';
      }
      
      setError(displayError);
    } finally {
      setLoading(false);
      isProcessingLoginRef.current = false;
    }
  };

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMagicLinkSent(false);
    setMagicLinkLoading(true);

    try {
      console.log(`[Login] 📧 Calling custom send-magic-link Edge Function for: ${magicLinkEmail}`);
      
      // Get Supabase URL from environment
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/send-magic-link`;
      
      console.log(`[Login] 🔗 Edge Function URL: ${edgeFunctionUrl}`);
      
      // Call custom Edge Function
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: magicLinkEmail,
        }),
      });

      const responseData = await response.json();
      
      console.log('[Login] 📧 Edge Function Response:', responseData);

      if (!response.ok) {
        console.error('[Login] ❌ Edge Function Error:', responseData);
        
        // Show detailed error to user
        const errorMessage = responseData.error || 'Failed to send magic link';
        const debugInfo = responseData.debug ? `\n\nDebug Info:\n${JSON.stringify(responseData.debug, null, 2)}` : '';
        
        throw new Error(`❌ ${errorMessage}${debugInfo}`);
      }

      console.log('[Login] ✅ Magic Link sent successfully');
      
      // Show success message with debug info
      console.log('✅ Magic Link Success:', responseData);
      
      setMagicLinkSent(true);
    } catch (err: any) {
      console.error('[Login] ❌ Magic link error:', err);
      
      // Show detailed error to user
      setError(err.message || 'Magic Link could not be sent. Please check console for details.');
      
      // Also log to console for easy debugging
      console.error('❌ Magic Link Error:', err);
    } finally {
      setMagicLinkLoading(false);
    }
  };

  // Magic Link Success Screen
  if (magicLinkSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-white to-amber-50 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-check-line text-3xl text-teal-600"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Magic Link gesendet!</h1>
              <p className="text-gray-600">
                Wir haben Ihnen einen Anmeldelink an <strong>{magicLinkEmail}</strong> gesendet.
              </p>
            </div>

            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                <div className="text-sm text-teal-800">
                  <p className="font-medium mb-2">Nächste Schritte:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Öffnen Sie Ihr E-Mail-Postfach</li>
                    <li>Klicken Sie auf den Anmeldelink</li>
                    <li>Sie werden automatisch angemeldet</li>
                  </ol>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setMagicLinkSent(false);
                setShowMagicLink(false);
              }}
              className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white py-3 rounded-lg font-medium hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg hover:shadow-xl whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
            >
              Zurück zur Anmeldung
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Magic Link Form
  if (showMagicLink) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-white to-amber-50 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-magic-line text-3xl text-teal-600"></i>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Magic Link Anmeldung</h1>
              <p className="text-gray-600">Melden Sie sich ohne Passwort an</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleMagicLinkSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail-Adresse
                </label>
                <input
                  type="email"
                  value={magicLinkEmail}
                  onChange={(e) => setMagicLinkEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  placeholder="ihre@email.de"
                />
              </div>

              <button
                type="submit"
                disabled={magicLinkLoading}
                className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white py-3 rounded-lg font-medium hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                {magicLinkLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    <span>Wird gesendet...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-mail-send-line text-xl"></i>
                    <span>Magic Link senden</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowMagicLink(false)}
                className="text-gray-600 hover:text-gray-800 text-sm font-medium cursor-pointer"
              >
                ← Zurück zur normalen Anmeldung
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal Login Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Willkommen zurück</h1>
            <p className="text-gray-600">Melden Sie sich in Ihrem Konto an</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                E-Mail-Adresse
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="ihre@email.de"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white py-3 rounded-lg font-medium hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl"></i>
                  <span>Wird angemeldet...</span>
                </>
              ) : (
                <span>Anmelden</span>
              )}
            </button>
          </form>

          <div className="mt-6">
            <button
              onClick={() => setShowMagicLink(true)}
              disabled={loading}
              className="w-full py-3 px-4 bg-white border-2 border-slate-200 text-slate-700 rounded-lg font-medium hover:border-slate-300 hover:bg-slate-50 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-mail-send-line text-lg"></i>
              <span>Mit Magic Link anmelden</span>
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Noch kein Konto?{' '}
              <Link to="/register" className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer">
                Jetzt registrieren
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm cursor-pointer">
              ← Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
